import React, { type CSSProperties, useMemo } from "react";
import Skeleton from "react-loading-skeleton";

import SafeECharts from "../../stats/SafeECharts";

interface YearMonthlyData {
  charged?: number;
  pending?: number;
  paidOut?: number;
}

interface MonthlyEarningsGraphProps {
  yearDataMap?: Record<number, Record<string, YearMonthlyData>>;
  isLoading?: boolean;
  style?: CSSProperties;
  className?: string;
  [key: string]: unknown;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const MonthlyEarningsGraph = ({
  yearDataMap = {},
  isLoading = false,
  style,
  className,
  ...EChartsProps
}: MonthlyEarningsGraphProps) => {
  const warningColor = getComputedStyle(document.documentElement).getPropertyValue("--warning").trim();
  const defaultColor = getComputedStyle(document.documentElement).getPropertyValue("--default").trim();
  const dangerColor = getComputedStyle(document.documentElement).getPropertyValue("--danger").trim();

  const series = useMemo(() => {
    const sortedYears = Object.keys(yearDataMap)
      .map(Number)
      .sort((a, b) => a - b);

    const palettes = [
      { charged: "#8fbcbb", pending: "#bf616a", paidOut: "#ebcb8b" }, // oldest
      { charged: "#5e81ac", pending: "#d08770", paidOut: "#a3be8c" }, // middle
      { charged: defaultColor, pending: dangerColor, paidOut: warningColor }, // newest
    ];

    // Align palettes: if fewer than 3 years, use the last N palettes
    const paletteOffset = 3 - sortedYears.length;

    return sortedYears.flatMap((year, idx) => {
      const palette = palettes[paletteOffset + idx] ?? palettes[2];
      const perMonth = yearDataMap[year] ?? {};

      const chargedData = Array.from({ length: 12 }, (_, i) => perMonth[String(i + 1)]?.charged ?? 0);
      const pendingData = Array.from({ length: 12 }, (_, i) => perMonth[String(i + 1)]?.pending ?? 0);
      const paidOutData = Array.from({ length: 12 }, (_, i) => perMonth[String(i + 1)]?.paidOut ?? 0);

      return [
        {
          color: palette.charged,
          name: `Charged ${year}`,
          type: "bar" as const,
          barMaxWidth: 25,
          data: chargedData,
          stack: String(year),
          tooltip: { valueFormatter: (n: number) => n + " €" },
        },
        {
          color: palette.pending,
          name: `Pending ${year}`,
          type: "bar" as const,
          barMaxWidth: 25,
          data: pendingData,
          stack: String(year),
          tooltip: { valueFormatter: (n: number) => n + " €" },
        },
        {
          color: palette.paidOut,
          name: `Paid out ${year}`,
          type: "bar" as const,
          barMaxWidth: 25,
          data: paidOutData,
          stack: String(year),
          tooltip: { valueFormatter: (n: number) => n + " €" },
        },
      ];
    });
  }, [yearDataMap, defaultColor, dangerColor, warningColor]);

  if (isLoading) return <Skeleton height={style?.height || 400} />;

  return (
    <SafeECharts
      {...EChartsProps}
      className={className}
      style={style}
      option={{
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        legend: {
          type: "scroll",
          top: 0,
        },
        grid: {
          containLabel: true,
          top: 40,
          right: 10,
          left: 10,
          bottom: 10,
        },
        yAxis: {
          type: "value",
          axisLabel: { formatter: (n: number) => n + " €" },
        },
        xAxis: {
          type: "category",
          data: MONTH_LABELS,
        },
        series,
      }}
    />
  );
};

export default MonthlyEarningsGraph;
